import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'

// ─── Tipos BD (reflejo del RPC wa_listar_comprobantes_pendientes) ─────────
export type EstadoComprobante =
  | 'pendiente'
  | 'en_revision_humana'
  | 'pedir_correcto'
  | 'validado_auto'
  | 'validado_manual'
  | 'rechazado'

export interface ComprobanteRow {
  id: string
  mensaje_id: string
  numero_whatsapp: string
  cedula: string
  cliente_nombre: string | null
  id_compra: number | null
  storage_path: string | null
  monto_extraido: number | null
  fecha_extraida: string | null  // ISO date 'YYYY-MM-DD'
  banco_extraido: string | null
  ultimos_4_extraidos: string | null
  titular_extraido: string | null
  numero_referencia: string | null
  tipo_transaccion: string | null
  confianza_ia: number | null  // 0..1
  modelo_usado: string | null
  match_bd: boolean | null
  match_razon: string | null
  estado: EstadoComprobante
  revisado_por: string | null
  revisado_at: string | null
  revision_nota: string | null
  creado_en: string
  // Enriquecimiento de point_general (JOIN del RPC)
  saldo_vencido_bd: number | null
  vence_bd: string | null
  cartera_bd: string | null
}

export interface AuditEntry {
  id: string
  editado_por: string | null
  campo: string
  valor_anterior: string | null
  valor_nuevo: string | null
  editado_at: string
}

// Campos editables (debe coincidir con whitelist del RPC M19)
export const CAMPOS_EDITABLES = [
  'monto_extraido',
  'fecha_extraida',
  'banco_extraido',
  'ultimos_4_extraidos',
  'titular_extraido',
  'numero_referencia',
  'tipo_transaccion',
] as const
export type CampoEditable = (typeof CAMPOS_EDITABLES)[number]

export type Decision = 'aprobar' | 'rechazar' | 'pedir_correcto'

// ─── Queries ──────────────────────────────────────────────────────────────

/**
 * Trae pendientes de revisión. Devuelve también los enriquecimientos desde point_general.
 */
export function useComprobantesPendientes(options: {
  limit?: number
  incluirResueltos?: boolean
} = {}) {
  const { limit = 50, incluirResueltos = false } = options
  return useQuery({
    queryKey: ['comprobantes', 'pendientes', { limit, incluirResueltos }],
    queryFn: async (): Promise<ComprobanteRow[]> => {
      const { data, error } = await supabase.rpc('wa_listar_comprobantes_pendientes', {
        limit_: limit,
        offset_: 0,
        incluir_resueltos: incluirResueltos,
      })
      if (error) throw error
      return (data ?? []) as ComprobanteRow[]
    },
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * Trae histórico de resueltos (y pendientes si se quiere).
 * Mismo RPC pero con incluir_resueltos=true + limit más alto.
 * Filtros fecha/estado/banco se aplican client-side.
 */
export function useHistoricoComprobantes(limit = 500) {
  return useQuery({
    queryKey: ['comprobantes', 'historico', { limit }],
    queryFn: async (): Promise<ComprobanteRow[]> => {
      const { data, error } = await supabase.rpc('wa_listar_comprobantes_pendientes', {
        limit_: limit,
        offset_: 0,
        incluir_resueltos: true,
      })
      if (error) throw error
      return (data ?? []) as ComprobanteRow[]
    },
    staleTime: 30_000,
  })
}

// ─── KPIs ────────────────────────────────────────────────────────────────

export interface KpiDia {
  dia: string                     // 'YYYY-MM-DD'
  total: number
  validados_auto: number
  validados_manual: number
  en_revision: number
  rechazados: number
  pct_auto: number | null         // 0..100
  latencia_p50_ms: number | null
  monto_total_validado: number
}

/**
 * KPIs diarios de comprobantes. Rango default: últimos 30 días.
 */
export function useKpisComprobantes(options: { desde?: string; hasta?: string } = {}) {
  const hoy = new Date()
  const hace30 = new Date(hoy.getTime() - 30 * 86_400_000)
  const desde = options.desde ?? hace30.toISOString().slice(0, 10)
  const hasta = options.hasta ?? hoy.toISOString().slice(0, 10)

  return useQuery({
    queryKey: ['comprobantes', 'kpis', { desde, hasta }],
    queryFn: async (): Promise<KpiDia[]> => {
      const { data, error } = await supabase.rpc('wa_kpis_comprobantes', {
        desde,
        hasta,
      })
      if (error) throw error
      return (data ?? []) as KpiDia[]
    },
    staleTime: 60_000,
  })
}

/**
 * Trae el audit log de un comprobante específico.
 */
export function useAuditComprobante(comprobanteId: string | null) {
  return useQuery({
    queryKey: ['comprobantes', 'audit', comprobanteId],
    queryFn: async (): Promise<AuditEntry[]> => {
      if (!comprobanteId) return []
      const { data, error } = await supabase.rpc('wa_listar_audit_comprobante', {
        comprobante_id: comprobanteId,
      })
      if (error) throw error
      return (data ?? []) as AuditEntry[]
    },
    enabled: !!comprobanteId,
    staleTime: 30_000,
  })
}

/**
 * Signed URL para la imagen del comprobante.
 * Va contra la Edge Function `comprobante-signed-url` que usa service_role
 * internamente (el bucket es RLS service_role-only).
 */
export function useSignedUrlComprobante(comprobanteId: string | null) {
  return useQuery({
    queryKey: ['storage', 'signed-url', comprobanteId],
    queryFn: async (): Promise<string | null> => {
      if (!comprobanteId) return null
      const { data, error } = await supabase.functions.invoke('comprobante-signed-url', {
        body: { comprobante_id: comprobanteId },
      })
      if (error) throw error
      if (!data?.signed_url) {
        if (data?.error === 'sin_imagen') return null
        throw new Error(data?.error ?? 'signed_url_desconocido')
      }
      return data.signed_url as string
    },
    enabled: !!comprobanteId,
    staleTime: 60 * 10 * 1000, // reusar durante 10 min aunque dure 15
  })
}

// ─── Mutations ────────────────────────────────────────────────────────────

/**
 * Edita campos inline del comprobante. Soporta múltiples campos a la vez.
 * El RPC ignora silente los campos no whitelisted.
 * El `editado_por` se toma automático del user logueado.
 */
export function useActualizarComprobante() {
  const qc = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: async (args: {
      id: string
      cambios: Partial<Record<CampoEditable, string | number | null>>
    }) => {
      const { data, error } = await supabase.rpc('wa_actualizar_campos_comprobante', {
        id: args.id,
        cambios: args.cambios,
        editado_por: user?.id ?? null,
      })
      if (error) throw error
      return data as { ok: boolean; cambios?: object; error?: string; estado_actual?: string }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['comprobantes', 'pendientes'] })
      qc.invalidateQueries({ queryKey: ['comprobantes', 'audit', vars.id] })
    },
  })
}

/**
 * Resolver un comprobante (aprobar / rechazar / pedir_correcto).
 * Motivo obligatorio en rechazar y pedir_correcto (lo valida el RPC).
 * El `revisado_por` se toma automático del user logueado.
 */
export function useResolverComprobante() {
  const qc = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: async (args: {
      id: string
      decision: Decision
      motivo: string | null
    }) => {
      const { data, error } = await supabase.rpc('wa_resolver_comprobante', {
        id: args.id,
        decision: args.decision,
        motivo: args.motivo ?? '',
        revisado_por: user?.id ?? null,
      })
      if (error) throw error
      return data as { ok: boolean; error?: string; estado?: string; decision?: string }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comprobantes', 'pendientes'] })
      qc.invalidateQueries({ queryKey: ['comprobantes', 'kpis'] })
    },
  })
}

// ─── Helpers derivados ────────────────────────────────────────────────────

/**
 * Deriva un "motivo" humano a partir de match_razon + estado + fecha.
 * Para mostrar el banner amarillo/rojo del panel derecho.
 */
export function derivarMotivo(c: ComprobanteRow): {
  key: string
  chip: string
  color: 'amber' | 'rose' | 'violet'
  descripcion: string
} {
  // Beneficiario no es Point (razones sociales conocidas)
  const titular = (c.titular_extraido ?? '').toLowerCase()
  const esPoint =
    titular.includes('point') ||
    titular.includes('compubussines') ||
    titular.includes('supermercado de computadoras')

  if (!esPoint && c.titular_extraido) {
    return {
      key: 'beneficiario_no_point',
      chip: '⛔ No es Point',
      color: 'violet',
      descripcion:
        'El beneficiario del comprobante no coincide con las razones sociales de Point registradas. Posible intento de fraude o cliente que depositó a otra cuenta.',
    }
  }

  // Fecha fuera del mes actual
  if (c.fecha_extraida) {
    const fechaExt = new Date(c.fecha_extraida)
    const hoy = new Date()
    const mesmo =
      fechaExt.getUTCFullYear() === hoy.getUTCFullYear() &&
      fechaExt.getUTCMonth() === hoy.getUTCMonth()
    if (!mesmo) {
      return {
        key: 'fecha_fuera_del_mes',
        chip: '📅 Fecha vieja',
        color: 'amber',
        descripcion: `La fecha del comprobante (${c.fecha_extraida}) no coincide con el mes actual. Posible intento de reutilizar comprobante antiguo o cliente que mandó el mes incorrecto.`,
      }
    }
  }

  // Confianza baja
  if (c.confianza_ia !== null && c.confianza_ia < 0.75) {
    return {
      key: 'manuscrito_ilegible',
      chip: '✎ Baja confianza',
      color: 'rose',
      descripcion: `La IA tiene confianza baja (${Math.round((c.confianza_ia ?? 0) * 100)}%) en los campos extraídos. Verificá visualmente antes de aprobar.`,
    }
  }

  // Fallback: match_bd falló
  return {
    key: c.match_razon ?? 'match_bd_fallido',
    chip: 'ℹ Revisar',
    color: 'amber',
    descripcion:
      c.match_razon ??
      'La IA no pudo validar automáticamente contra el saldo del cliente. Revisá los campos extraídos.',
  }
}

/**
 * Minutos (y horas) de espera desde que llegó el comprobante.
 */
export function calcularEspera(creado_en: string): { horas: number; minutos: number; urgente: boolean } {
  const diff = Date.now() - new Date(creado_en).getTime()
  const totalMin = Math.max(0, Math.floor(diff / 60_000))
  const horas = Math.floor(totalMin / 60)
  const minutos = totalMin % 60
  return { horas, minutos, urgente: horas >= 2 }
}
